import { useEffect, useRef, useState, type TouchEvent as ReactTouchEvent } from "react";
import { Link } from "react-router-dom";
import type { VideoItem } from "@/mocks/videos";
import { formatCount } from "@/lib/format";

interface HeroCarouselProps {
  items: VideoItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (paused || items.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % items.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [paused, items.length]);

  const go = (dir: number) => {
    setIndex((i) => (i + dir + items.length) % items.length);
  };

  const onTouchStart = (e: ReactTouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: ReactTouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(delta > 0 ? -1 : 1);
    }
    touchStartX.current = null;
  };

  if (items.length === 0) return null;
  const active = items[index];

  return (
    <section
      className="relative w-full h-[420px] md:h-[560px] rounded-xl overflow-hidden bg-foreground-900"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} - 快手热门短视频`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 via-foreground-950/40 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-12 w-full max-w-3xl">
        <div className="flex items-center gap-2 mb-4">
          <span className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
            热门推荐
          </span>
          <Link
            to={`/category/${active.categoryId}`}
            className="px-3 py-1 rounded-full bg-background-50/15 backdrop-blur text-background-50 text-xs hover:bg-background-50/25 transition-colors"
          >
            {active.categoryName}
          </Link>
        </div>
        <h2 className="text-2xl md:text-4xl font-bold text-background-50 leading-tight mb-3">
          {active.title}
        </h2>
        <p className="text-sm md:text-base text-background-100/80 line-clamp-2 mb-6 max-w-2xl">
          {active.description}
        </p>
        <div className="flex flex-wrap items-center gap-4">
          <Link
            to={`/detail/${active.id}`}
            className="flex items-center gap-2 h-11 px-6 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-play-fill text-lg"></i>
            立即观看
          </Link>
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full overflow-hidden bg-background-200">
              <img src={active.authorAvatar} alt={active.author} className="w-full h-full object-cover object-top" />
            </span>
            <span className="text-sm text-background-50">{active.author}</span>
            <span className="text-xs text-background-100/70 ml-1">
              {formatCount(active.views)} 播放
            </span>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(-1)}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background-50/80 hover:bg-background-50 items-center justify-center text-foreground-800 text-xl transition-colors cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line"></i>
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-background-50/80 hover:bg-background-50 items-center justify-center text-foreground-800 text-xl transition-colors cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line"></i>
      </button>

      <div className="absolute bottom-5 right-6 z-20 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`切换到第${i + 1}张`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-6 bg-primary-500" : "w-1.5 bg-background-50/60"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}